import { isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import config from 'constants/config'
import { PRIMARY_TRAVELLER_FORM_ID } from 'constants/checkout'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { arrayToMap, groupBy, sortByAll } from 'lib/array/arrayUtils'

const ROOM_GUEST_FIELD_PREFIX = 'checkoutRoomGuest'

export type RoomGuestItem = App.Checkout.BedbankHotelItem | App.Checkout.InstantBookingLEHotelItem

function isRoomGuestFieldsOfferType(offerType: App.OfferType | undefined) {
  return !!offerType &&
    config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED &&
    isEnabledForFeature(config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED_LIST, offerType)
}

export function isRoomGuestFieldsOffer(offer: App.AnyOffer | undefined) {
  const offerType = offer?.type
  if (!isRoomGuestFieldsOfferType(offerType)) return false

  return offerType !== OFFER_TYPE_HOTEL || isFlashHotelOffer(offer)
}

function isFlashRoomGuestItem(
  item: RoomGuestItem,
  offers: Record<string, App.AnyOffer | undefined>,
) {
  return !isBedbankItem(item) && isFlashHotelOffer(offers[item.offerId])
}

export function getCustomerOverlappingRoomGuestItems(
  items: Array<RoomGuestItem>,
  offers: Record<string, App.AnyOffer | undefined>,
  includeFlashOverlappingRoomGuests: boolean,
) {
  if (includeFlashOverlappingRoomGuests) return items
  return items.filter(item => !isFlashRoomGuestItem(item, offers))
}

export function getRoomGuestFieldKeys(itemId: string) {
  return {
    firstName: `${ROOM_GUEST_FIELD_PREFIX}_${itemId}_firstName`,
    lastName: `${ROOM_GUEST_FIELD_PREFIX}_${itemId}_lastName`,
  }
}

export function isRoomGuestFieldKey(fieldKey: string) {
  return fieldKey.startsWith(ROOM_GUEST_FIELD_PREFIX)
}

export function getLeadGuestTravellerForm(
  itemId: string,
  travellerForms: Array<App.Checkout.TravellerForm>,
  formSchema: App.Checkout.FormSchema | undefined,
) {
  if (!formSchema) return undefined

  const formsById = arrayToMap(travellerForms, form => form.id)
  const leadFormId = Object.keys(formSchema.properties).find((formId) => (
    formSchema.properties[formId]?.relatedItem?.id === itemId
  ))

  return leadFormId ? formsById.get(leadFormId) : undefined
}

export function normaliseRoomGuestName(value: unknown) {
  if (typeof value !== 'string') return ''
  return value.trim().toLocaleLowerCase()
}

export function isSameRoomGuestName(
  firstName: unknown,
  lastName: unknown,
  otherFirstName: unknown,
  otherLastName: unknown,
) {
  return normaliseRoomGuestName(firstName) === normaliseRoomGuestName(otherFirstName) &&
    normaliseRoomGuestName(lastName) === normaliseRoomGuestName(otherLastName)
}

export function doRoomGuestStaysOverlap(left: RoomGuestItem, right: RoomGuestItem) {
  return left.checkIn < right.checkOut && right.checkIn < left.checkOut
}

type RoomGuestPropertyOfferMappings = App.OfferState['propertyOfferMappings']

function getOfferPropertyId(offer: App.AnyOffer | App.BedbankOffer | undefined): string | undefined {
  if (!offer || !('property' in offer) || !offer.property || !('id' in offer.property)) return undefined
  return offer.property.id
}

export function getRoomGuestPropertyMapping(
  item: RoomGuestItem,
  propertyOfferMappings: RoomGuestPropertyOfferMappings = {},
): App.PropertyOfferMapping | undefined {
  const directMapping = propertyOfferMappings[item.offerId]?.mapping
  if (directMapping?.propertyId) return directMapping

  return Object.values(propertyOfferMappings).find((record) => {
    const mapping = record?.mapping
    if (!mapping) return false
    return mapping.lpcOfferIds.includes(item.offerId) || mapping.bedbankPropertyIds.includes(item.offerId)
  })?.mapping
}

export function getRoomGuestPropertyId(
  item: RoomGuestItem,
  offers: Record<string, App.AnyOffer | undefined> = {},
  bedbankOffers: Record<string, App.BedbankOffer | undefined> = {},
  propertyOfferMappings: RoomGuestPropertyOfferMappings = {},
): string | undefined {
  const mappedPropertyId = getRoomGuestPropertyMapping(item, propertyOfferMappings)?.propertyId
  if (mappedPropertyId) return mappedPropertyId

  const offer = isBedbankItem(item) ? (bedbankOffers[item.offerId] ?? offers[item.offerId]) : offers[item.offerId]
  return getOfferPropertyId(offer) ?? (isBedbankItem(item) ? item.offerId : undefined)
}

function doesOverlappingRoomGuestPairNeedPropertyMapping(
  left: RoomGuestItem,
  right: RoomGuestItem,
  offers: Record<string, App.AnyOffer | undefined>,
  bedbankOffers: Record<string, App.BedbankOffer | undefined>,
  propertyOfferMappings: RoomGuestPropertyOfferMappings,
) {
  if (!doRoomGuestStaysOverlap(left, right)) return false

  const leftMappedPropertyId = getRoomGuestPropertyMapping(left, propertyOfferMappings)?.propertyId
  const rightMappedPropertyId = getRoomGuestPropertyMapping(right, propertyOfferMappings)?.propertyId
  if (leftMappedPropertyId || rightMappedPropertyId) return false

  const leftNativePropertyId = getRoomGuestPropertyId(left, offers, bedbankOffers)
  const rightNativePropertyId = getRoomGuestPropertyId(right, offers, bedbankOffers)
  return !(leftNativePropertyId && leftNativePropertyId === rightNativePropertyId)
}

export function getRoomGuestPropertyMappingFetchItem(
  items: Array<RoomGuestItem>,
  offers: Record<string, App.AnyOffer | undefined> = {},
  bedbankOffers: Record<string, App.BedbankOffer | undefined> = {},
  propertyOfferMappings: RoomGuestPropertyOfferMappings = {},
): RoomGuestItem | undefined {
  for (let leftIndex = 0; leftIndex < items.length; leftIndex++) {
    for (let rightIndex = leftIndex + 1; rightIndex < items.length; rightIndex++) {
      const left = items[leftIndex]
      const right = items[rightIndex]
      if (!doesOverlappingRoomGuestPairNeedPropertyMapping(left, right, offers, bedbankOffers, propertyOfferMappings)) continue
      if (propertyOfferMappings[left.offerId] || propertyOfferMappings[right.offerId]) continue

      if (!isBedbankItem(left)) return left
      if (!isBedbankItem(right)) return right
      return left
    }
  }
}

export function isRoomGuestPropertyMappingPending(
  items: Array<RoomGuestItem>,
  offers: Record<string, App.AnyOffer | undefined> = {},
  bedbankOffers: Record<string, App.BedbankOffer | undefined> = {},
  propertyOfferMappings: RoomGuestPropertyOfferMappings = {},
) {
  if (getRoomGuestPropertyMappingFetchItem(items, offers, bedbankOffers, propertyOfferMappings)) return true

  for (let leftIndex = 0; leftIndex < items.length; leftIndex++) {
    for (let rightIndex = leftIndex + 1; rightIndex < items.length; rightIndex++) {
      const left = items[leftIndex]
      const right = items[rightIndex]
      if (!doesOverlappingRoomGuestPairNeedPropertyMapping(left, right, offers, bedbankOffers, propertyOfferMappings)) continue
      if (propertyOfferMappings[left.offerId]?.fetching || propertyOfferMappings[right.offerId]?.fetching) return true
    }
  }

  return false
}

export function getRoomGuestPropertyIdByItemId(
  items: Array<RoomGuestItem>,
  offers: Record<string, App.AnyOffer | undefined> = {},
  bedbankOffers: Record<string, App.BedbankOffer | undefined> = {},
  propertyOfferMappings: RoomGuestPropertyOfferMappings = {},
): Map<string, string | undefined> {
  return arrayToMap(items, item => item.itemId, item => getRoomGuestPropertyId(item, offers, bedbankOffers, propertyOfferMappings))
}

function isSameRoomGuestProperty(
  left: RoomGuestItem,
  right: RoomGuestItem,
  propertyIdByItemId: Map<string, string | undefined>,
) {
  const leftPropertyId = propertyIdByItemId.get(left.itemId)
  const rightPropertyId = propertyIdByItemId.get(right.itemId)
  return !!leftPropertyId && leftPropertyId === rightPropertyId
}

export function getOverlappingSamePropertyRoomGuestItems(
  items: Array<RoomGuestItem>,
  propertyIdByItemId: Map<string, string | undefined>,
): Array<RoomGuestItem> {
  return items.filter((item) => items.some((other) => {
    if (other.itemId === item.itemId) return false
    if (!isSameRoomGuestProperty(item, other, propertyIdByItemId)) return false
    return doRoomGuestStaysOverlap(item, other)
  }))
}

export function getPrimaryRoomGuestItemId(
  itemIds: Array<string>,
  namesByItemId: Map<string, { firstName?: string, lastName?: string }>,
  primaryForm: App.Checkout.TravellerForm | undefined,
): string | undefined {
  const matchingItemId = itemIds.find((itemId) => {
    const name = namesByItemId.get(itemId)
    if (!name) return false
    return isSameRoomGuestName(name.firstName, name.lastName, primaryForm?.data.firstName, primaryForm?.data.lastName)
  })

  return matchingItemId
}

export interface RoomGuestPropertyMetadata {
  roomCount: number
  roomNumber: number
}

function getRoomGuestPropertyGroupKey(item: RoomGuestItem): string {
  return `${item.offerId}-${item.checkIn}-${item.checkOut}`
}

export function getRoomGuestPropertyMetadataByItemId(
  roomGuestItems: Array<RoomGuestItem>,
): Map<string, RoomGuestPropertyMetadata> {
  const countsByGroup = new Map<string, number>()
  for (const item of roomGuestItems) {
    const groupKey = getRoomGuestPropertyGroupKey(item)
    countsByGroup.set(groupKey, (countsByGroup.get(groupKey) ?? 0) + 1)
  }

  const roomNumbersByGroup = new Map<string, number>()
  const result = new Map<string, RoomGuestPropertyMetadata>()
  for (const item of roomGuestItems) {
    const groupKey = getRoomGuestPropertyGroupKey(item)
    const roomNumber = (roomNumbersByGroup.get(groupKey) ?? 0) + 1
    roomNumbersByGroup.set(groupKey, roomNumber)
    result.set(item.itemId, {
      roomCount: countsByGroup.get(groupKey) ?? 1,
      roomNumber,
    })
  }

  return result
}

export function getRoomGuestItemHotelName(
  item: RoomGuestItem,
  offers: Record<string, App.AnyOffer | undefined>,
  bedbankOffers: Record<string, App.BedbankOffer | undefined>,
): string {
  if (isBedbankItem(item)) {
    const offer = bedbankOffers[item.offerId]
    return offer?.property?.name ?? offer?.name ?? ''
  }

  if (isInstantBookingLEHotelItem(item)) {
    const offer = offers[item.offerId]
    if (!offer) return ''
    if ('property' in offer && offer.property?.name) {
      return offer.property.name
    }
    if ('name' in offer && typeof offer.name === 'string') {
      return offer.name
    }
  }

  return ''
}

export function getRoomGuestNames({
  itemId,
  primaryForm,
  travellerForms,
  formSchema,
  fallbackFirstName,
  fallbackLastName,
  isEnabled,
  isPrimaryRoom,
}: {
  itemId: string
  primaryForm: App.Checkout.TravellerForm | undefined
  travellerForms?: Array<App.Checkout.TravellerForm>
  formSchema?: App.Checkout.FormSchema
  fallbackFirstName: string | undefined
  fallbackLastName: string | undefined
  isEnabled: boolean
  isPrimaryRoom: boolean
}) {
  if (!isEnabled) {
    return {
      firstName: fallbackFirstName,
      lastName: fallbackLastName,
    }
  }

  const leadForm = getLeadGuestTravellerForm(
    itemId,
    travellerForms ?? (primaryForm ? [primaryForm] : []),
    formSchema,
  )
  if (leadForm) {
    return {
      firstName: leadForm.data.firstName,
      lastName: leadForm.data.lastName,
    }
  }

  const fieldKeys = getRoomGuestFieldKeys(itemId)
  return {
    firstName: primaryForm?.data[fieldKeys.firstName] ?? (isPrimaryRoom ? fallbackFirstName : undefined),
    lastName: primaryForm?.data[fieldKeys.lastName] ?? (isPrimaryRoom ? fallbackLastName : undefined),
  }
}

export function getResolvedRoomGuestNamesByItemId(
  items: Array<RoomGuestItem>,
  travellerForms: Array<App.Checkout.TravellerForm>,
  formSchema: App.Checkout.FormSchema | undefined,
  defaultFirstRoomToPrimary = true,
) {
  const primaryForm = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)
  return arrayToMap(items, item => item.itemId, (item, index) =>
    getRoomGuestNames({
      itemId: item.itemId,
      primaryForm,
      travellerForms,
      formSchema,
      fallbackFirstName: primaryForm?.data.firstName,
      fallbackLastName: primaryForm?.data.lastName,
      isEnabled: true,
      isPrimaryRoom: defaultFirstRoomToPrimary && index === 0,
    }),
  )
}

export function shouldDefaultFirstCollectedRoomToPrimary(
  eligibleItems: Array<RoomGuestItem>,
  collectedItems: Array<RoomGuestItem>,
  propertyIdByItemId: Map<string, string | undefined>,
) {
  if (collectedItems.length === 0 || collectedItems.length !== eligibleItems.length) return false

  const propertyIds = new Set<string>()
  for (const item of collectedItems) {
    const propertyId = propertyIdByItemId.get(item.itemId)
    if (propertyId) propertyIds.add(propertyId)
  }

  return propertyIds.size === 1
}

export function getMappedRoomGuestEligibleItems(
  eligibleItems: Array<RoomGuestItem>,
  offers: Record<string, App.AnyOffer | undefined>,
  overlappingRoomGuestNamesEnabled = false,
  includeFlashOverlappingRoomGuests = false,
) {
  if (!overlappingRoomGuestNamesEnabled) return []
  return getCustomerOverlappingRoomGuestItems(eligibleItems, offers, includeFlashOverlappingRoomGuests)
}

export function hasDuplicateOverlappingRoomGuestName({
  items,
  item,
  firstName,
  lastName,
  namesByItemId,
  propertyIdByItemId,
}: {
  items: Array<RoomGuestItem>
  item: RoomGuestItem
  firstName: unknown
  lastName: unknown
  namesByItemId: Map<string, { firstName?: string, lastName?: string }>
  propertyIdByItemId: Map<string, string | undefined>
}) {
  if (!normaliseRoomGuestName(firstName) || !normaliseRoomGuestName(lastName)) return false

  return items.some((other) => {
    if (other.itemId === item.itemId) return false
    if (!isSameRoomGuestProperty(item, other, propertyIdByItemId)) return false
    if (!doRoomGuestStaysOverlap(item, other)) return false

    const otherName = namesByItemId.get(other.itemId)
    if (!otherName) return false
    if (!normaliseRoomGuestName(otherName.firstName) || !normaliseRoomGuestName(otherName.lastName)) return false

    return isSameRoomGuestName(firstName, lastName, otherName.firstName, otherName.lastName)
  })
}

export function hasAnyDuplicateOverlappingRoomGuestName(
  items: Array<RoomGuestItem>,
  namesByItemId: Map<string, { firstName?: string, lastName?: string }>,
  propertyIdByItemId: Map<string, string | undefined>,
) {
  return items.some((item) => {
    const name = namesByItemId.get(item.itemId)
    if (!name) return false

    return hasDuplicateOverlappingRoomGuestName({
      items,
      item,
      firstName: name.firstName,
      lastName: name.lastName,
      namesByItemId,
      propertyIdByItemId,
    })
  })
}

export function getRoomGuestItems(
  items: Array<App.Checkout.AccommodationItem>,
  offers: Record<string, App.AnyOffer | undefined>,
): Array<RoomGuestItem> {
  const roomGuestItems = items.filter((item): item is RoomGuestItem => {
    return (isBedbankItem(item) && isRoomGuestFieldsOfferType(OFFER_TYPE_BED_BANK)) ||
      (isInstantBookingLEHotelItem(item) && isRoomGuestFieldsOffer(offers[item.offerId]))
  })

  return sortRoomGuestItemsByCartDisplayOrder(roomGuestItems)
}

export function getCheckoutRoomGuestItems(
  items: Array<App.Checkout.AccommodationItem>,
  offers: Record<string, App.AnyOffer | undefined>,
  collectAllMultiRoomGuests: boolean,
  bedbankOffers: Record<string, App.BedbankOffer | undefined> = {},
  propertyOfferMappings: RoomGuestPropertyOfferMappings = {},
  overlappingRoomGuestNamesEnabled = false,
  includeFlashOverlappingRoomGuests = false,
): Array<RoomGuestItem> {
  const eligibleItems = getRoomGuestItems(items, offers)
  if (collectAllMultiRoomGuests) return eligibleItems
  if (!overlappingRoomGuestNamesEnabled) return []

  const customerItems = getCustomerOverlappingRoomGuestItems(
    eligibleItems,
    offers,
    includeFlashOverlappingRoomGuests,
  )
  return getOverlappingSamePropertyRoomGuestItems(
    customerItems,
    getRoomGuestPropertyIdByItemId(customerItems, offers, bedbankOffers, propertyOfferMappings),
  )
}

function getRoomGuestItemBookingKey(item: RoomGuestItem) {
  return `${item.offerId}-${item.checkIn}-${item.checkOut}`
}

export function sortRoomGuestItemsByCartDisplayOrder(items: Array<RoomGuestItem>): Array<RoomGuestItem> {
  const itemGroups = Array.from(groupBy(items, getRoomGuestItemBookingKey).values()).map((items, index) => ({
    index,
    items,
    startDate: items[0].checkIn,
  }))

  return sortByAll(itemGroups, [
    { selector: itemGroup => itemGroup.startDate, direction: 'asc' },
    { selector: itemGroup => itemGroup.index, direction: 'asc' },
  ]).flatMap(itemGroup => itemGroup.items)
}

export function preserveRoomGuestFieldValues({
  previousData,
  nextData,
}: {
  previousData: App.Checkout.TravellerFormData
  nextData: App.Checkout.TravellerFormData
}): App.Checkout.TravellerFormData {
  const roomGuestData = Object.fromEntries(
    Object.entries(previousData).filter(([fieldKey]) => {
      return isRoomGuestFieldKey(fieldKey)
    }),
  ) as App.Checkout.TravellerFormData

  return {
    ...roomGuestData,
    ...nextData,
  }
}
